import { useEffect } from "react";
import "@fontsource/ibm-plex-sans/400.css";
import "@fontsource/ibm-plex-sans/500.css";
import "@fontsource/ibm-plex-sans/600.css";
import "@fontsource/ibm-plex-sans/700.css";
import "@fontsource/ibm-plex-mono/400.css";
import "@fontsource/ibm-plex-mono/500.css";
import "@fontsource/source-serif-4/400.css";
import "@fontsource/source-serif-4/600.css";
import "@fontsource/source-serif-4/600-italic.css";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation , Navigate, useParams } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import Nav from "@/site/components/Nav";
import Footer from "@/site/components/Footer";
import Home from "@/site/pages/Home";
import PricingPage from "@/site/pages/PricingPage";
import SecurityPage from "@/site/pages/SecurityPage";
import ChangelogPage from "@/site/pages/ChangelogPage";
import AboutPage from "@/site/pages/AboutPage";
import LegalPage from "@/site/pages/LegalPage";
import PilotPage from "@/site/pages/PilotPage";
import ContactPage from "@/site/pages/ContactPage";
import DownloadsPage from "@/site/pages/DownloadsPage";
import AdminLeadsPage from "@/site/pages/AdminLeadsPage";
import BlogPage from "@/site/pages/BlogPage";
import ArticlePage from "@/site/pages/ArticlePage";
import SubscribePage from "@/site/pages/SubscribePage";
import NotFound from "@/site/pages/NotFound";
import { CalculatorsHub, CalculatorPage } from "@/site/pages/CalculatorsPage";
import { captureAttribution, installVendors, pageview } from "@/site/analytics";

function ScrollAndTrack() {
  const loc = useLocation();
  useEffect(() => {
    if (loc.hash) {
      const el = document.getElementById(loc.hash.slice(1));
      if (el) { setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 60); return; }
    }
    window.scrollTo({ top: 0 });
  }, [loc.pathname, loc.hash]);
  useEffect(() => { pageview(loc.pathname); }, [loc.pathname]);
  return null;
}

function WritingRedirect() { const { slug } = useParams(); return <Navigate to={`/blog/${slug}`} replace />; }

export default function App() {
  useEffect(() => { captureAttribution(); installVendors(); }, []);
  return (
    <HelmetProvider>
      <BrowserRouter>
        <ScrollAndTrack />
        <a href="#main" className="skip-link">Skip to content</a>
        <Nav />
        <main id="main">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/calculators" element={<CalculatorsHub />} />
            <Route path="/calculators/:slug" element={<CalculatorPage />} />
            <Route path="/security" element={<SecurityPage />} />
            <Route path="/changelog" element={<ChangelogPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/subscribe" element={<SubscribePage />} />
            <Route path="/blog/:slug" element={<ArticlePage />} />
            {/* Old name, kept so nothing shared breaks. */}
            <Route path="/writing" element={<Navigate to="/blog" replace />} />
            <Route path="/writing/:slug" element={<WritingRedirect />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/pilot" element={<PilotPage />} />
            <Route path="/contact" element={<ContactPage />} />
            {/* No website checkout at launch: old /buy links go to the pilot request */}
            <Route path="/buy" element={<Navigate to="/pilot" replace />} />
            <Route path="/privacy" element={<LegalPage kind="privacy" />} />
            <Route path="/terms" element={<LegalPage kind="terms" />} />
            <Route path="/downloads" element={<DownloadsPage />} />
            <Route path="/admin/leads" element={<AdminLeadsPage />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
        <Footer />
        <Toaster position="bottom-right" />
      </BrowserRouter>
    </HelmetProvider>
  );
}
